import { Nostalgist } from "nostalgist";
import type { NESButton, PlaybackSpeed, ReplayAction } from "../server/types";

type SaveStateData = {
  state: Blob;
  frameCount: number;
};

/** SMB ignores gameplay input until Start is pressed at the title screen, so a
 *  replay from a fresh power-on has to sit through the title and press Start. */
const TITLE_SKIP: ReplayAction[] = [
  { frames: 180, buttons: [] },
  { frames: 10, buttons: ["start"] },
  { frames: 240, buttons: [] },
];

export class EmulatorController {
  private nostalgist: Nostalgist | null = null;
  private canvas: HTMLCanvasElement | null = null;
  private totalFrames = 0;
  private saveStates = new Map<string, SaveStateData>();
  private speed: PlaybackSpeed = "1x";
  private useFrameAdvanceFallback = false;

  async init(canvas: HTMLCanvasElement): Promise<void> {
    this.canvas = canvas;

    this.nostalgist = await Nostalgist.launch({
      core: "fceumm",
      rom: new URL("/smb.nes", window.location.origin).href,
      element: canvas,
    });

    console.log("[Emulator] Launched successfully");

    // Test if FRAMEADVANCE works
    try {
      this.nostalgist.pause();
      this.nostalgist.sendCommand("FRAMEADVANCE");
      await new Promise((r) => setTimeout(r, 50));
      this.nostalgist.resume();
      console.log("[Emulator] FRAMEADVANCE supported");
    } catch {
      console.warn("[Emulator] FRAMEADVANCE not supported, using fallback");
      this.useFrameAdvanceFallback = true;
    }
  }

  setSpeed(speed: PlaybackSpeed) {
    this.speed = speed;
  }

  getSpeed(): PlaybackSpeed {
    return this.speed;
  }

  private getDelay(): number {
    switch (this.speed) {
      case "1x": return 16;
      case "2x": return 8;
      case "4x": return 4;
      case "instant": return 0;
    }
  }

  private stepOneFrame() {
    if (!this.nostalgist) return;

    if (this.useFrameAdvanceFallback) {
      this.runMainLoop();
    } else {
      this.nostalgist.sendCommand("FRAMEADVANCE");
    }
  }

  /** Run the Emscripten main loop function directly — this runs the full
   *  render pipeline so the WebGL canvas is actually updated. FRAMEADVANCE
   *  may only advance the core without flushing the video driver in WASM. */
  private runMainLoop() {
    if (!this.nostalgist) return;
    try {
      const emu = (this.nostalgist as any).getEmscripten?.();
      if (emu?.Browser?.mainLoop?.func) {
        emu.Browser.mainLoop.func();
      } else {
        this.nostalgist.sendCommand("FRAMEADVANCE");
      }
    } catch {
      this.nostalgist.sendCommand("FRAMEADVANCE");
    }
  }

  async advanceFrames(frames: number, buttons: NESButton[]): Promise<{ screenshot: string; totalFrames: number }> {
    if (!this.nostalgist) throw new Error("Emulator not initialized");

    const delay = this.getDelay();

    this.nostalgist.pause();

    // Press buttons down
    for (const btn of buttons) {
      this.nostalgist.pressDown(btn);
    }

    // Advance frame by frame
    for (let i = 0; i < frames; i++) {
      this.stepOneFrame();
      this.totalFrames++;

      if (delay > 0) {
        await new Promise((r) => setTimeout(r, delay));
      } else {
        // Yield to event loop so Emscripten can process
        await new Promise((r) => setTimeout(r, 0));
      }
    }

    // Release buttons
    for (const btn of buttons) {
      this.nostalgist.pressUp(btn);
    }

    const screenshot = await this.captureScreenshot();
    return { screenshot, totalFrames: this.totalFrames };
  }

  async saveState(slotName: string): Promise<{ totalFrames: number }> {
    if (!this.nostalgist) throw new Error("Emulator not initialized");

    const state = await this.nostalgist.saveState();
    this.saveStates.set(slotName, {
      state: state.state,
      frameCount: this.totalFrames,
    });

    return { totalFrames: this.totalFrames };
  }

  async loadState(slotName: string): Promise<{ screenshot: string; totalFrames: number }> {
    if (!this.nostalgist) throw new Error("Emulator not initialized");

    const saved = this.saveStates.get(slotName);
    if (!saved) throw new Error(`No save state named "${slotName}"`);

    await this.nostalgist.loadState(saved.state);
    this.totalFrames = saved.frameCount;

    // After loading state the emulator needs to render a frame before
    // RetroArch's SCREENSHOT command can capture anything. Kick one frame
    // forward so the framebuffer is populated.
    this.nostalgist.pause();
    this.stepOneFrame();
    await new Promise((r) => setTimeout(r, 50));

    const screenshot = await this.captureScreenshot();
    return { screenshot, totalFrames: this.totalFrames };
  }

  async getScreenshot(): Promise<{ screenshot: string; totalFrames: number }> {
    return {
      screenshot: await this.captureScreenshot(),
      totalFrames: this.totalFrames,
    };
  }

  getTotalFrames(): number {
    return this.totalFrames;
  }

  getSaveStateNames(): string[] {
    return Array.from(this.saveStates.keys());
  }

  getSaveStateFrame(slotName: string): number | undefined {
    return this.saveStates.get(slotName)?.frameCount;
  }

  private async captureScreenshot(): Promise<string> {
    if (!this.nostalgist) return "";

    // Try Nostalgist's built-in screenshot (RetroArch SCREENSHOT command).
    // This writes a PNG via the virtual filesystem, bypassing the WebGL
    // preserveDrawingBuffer issue that makes canvas.toDataURL() blank.
    // If the first attempt fails (e.g. after loadState when the render
    // buffer hasn't been populated), kick a frame and retry once.
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const blob = await this.nostalgist.screenshot();
        const buffer = await blob.arrayBuffer();
        const bytes = new Uint8Array(buffer);
        let binary = "";
        for (let i = 0; i < bytes.length; i++) {
          binary += String.fromCharCode(bytes[i]!);
        }
        return btoa(binary);
      } catch (e) {
        if (attempt === 0) {
          console.warn("[Emulator] Screenshot attempt 1 failed, kicking a frame and retrying:", e);
          // Advance one frame to populate the render buffer
          this.stepOneFrame();
          await new Promise((r) => setTimeout(r, 100));
        } else {
          console.warn("[Emulator] Screenshot attempt 2 failed, using canvas fallback:", e);
        }
      }
    }

    // Fallback: try canvas.toDataURL
    if (this.canvas) {
      try {
        const dataUrl = this.canvas.toDataURL("image/png");
        const b64 = dataUrl.replace(/^data:image\/png;base64,/, "");
        if (b64.length > 100) return b64; // sanity check it's not blank
      } catch {
        // ignore
      }
    }

    return "";
  }

  pause() {
    this.nostalgist?.pause();
  }

  /** Save the current state as the replay start point */
  async saveReplayStart(): Promise<void> {
    if (!this.nostalgist) return;
    const state = await this.nostalgist.saveState();
    this.saveStates.set("_replay_start", {
      state: state.state,
      frameCount: this.totalFrames,
    });
  }

  /**
   * Replay a list of advance_frames actions and record as a WebM video.
   *
   * Previous approaches (paused + FRAMEADVANCE / Browser.mainLoop.func()) all
   * failed because the WebGL canvas never visually updates when the emulator
   * is paused. Instead we **resume** the emulator so it renders naturally at
   * 60 fps, schedule button presses/releases at the right frame boundaries,
   * and record the live canvas via a 2D mirror + captureStream.
   */
  async replayAndRecord(
    actions: ReplayAction[],
    onProgress?: (step: number, total: number) => void
  ): Promise<Blob | null> {
    return this.runReplay(actions, true, onProgress);
  }

  /**
   * Replay the same action list without recording — used for the watch-only
   * demo. Saves a start point if none exists, and prepends the title-screen
   * skip when the emulator is at a fresh power-on.
   */
  async replay(
    actions: ReplayAction[],
    onProgress?: (step: number, total: number) => void
  ): Promise<boolean> {
    if (!this.nostalgist || !this.canvas) return false;

    if (!this.saveStates.has("_replay_start")) {
      await this.saveReplayStart();
    }
    const combined =
      this.getTotalFrames() < 60 ? [...TITLE_SKIP, ...actions] : actions;

    await this.runReplay(combined, false, onProgress);
    return true;
  }

  private async runReplay(
    actions: ReplayAction[],
    record: boolean,
    onProgress?: (step: number, total: number) => void
  ): Promise<Blob | null> {
    if (!this.nostalgist || !this.canvas) return null;

    const initialState = this.saveStates.get("_replay_start");
    if (!initialState) {
      console.error("[Emulator] No _replay_start state saved");
      return null;
    }

    // Load the starting state and let it render one frame
    await this.nostalgist.loadState(initialState.state);
    this.nostalgist.resume();
    await new Promise((r) => setTimeout(r, 100));

    // Build a flat timeline: at which frame do we press/release which buttons?
    // Each entry: { frameOffset, pressButtons, releaseButtons }
    type FrameEvent = {
      frame: number;
      press: string[];
      release: string[];
    };
    const events: FrameEvent[] = [];
    let totalReplayFrames = 0;
    for (const action of actions) {
      // At the start of this action, press its buttons
      events.push({
        frame: totalReplayFrames,
        press: action.buttons as string[],
        release: [],
      });
      totalReplayFrames += action.frames;
      // At the end, release them
      events.push({
        frame: totalReplayFrames,
        press: [],
        release: action.buttons as string[],
      });
    }

    // Create a 2D mirror canvas for recording (WebGL preserveDrawingBuffer
    // is false, so captureStream on the GL canvas misses frames).
    let recordCanvas: HTMLCanvasElement | null = null;
    let ctx: CanvasRenderingContext2D | null = null;
    let recorder: MediaRecorder | null = null;
    let recordingDone: Promise<Blob> | null = null;

    if (record) {
      recordCanvas = document.createElement("canvas");
      recordCanvas.width = this.canvas.width;
      recordCanvas.height = this.canvas.height;
      ctx = recordCanvas.getContext("2d")!;

      const stream = recordCanvas.captureStream(60);
      const mimeType = MediaRecorder.isTypeSupported("video/webm;codecs=vp9")
        ? "video/webm;codecs=vp9"
        : "video/webm";
      recorder = new MediaRecorder(stream, { mimeType });
      const chunks: Blob[] = [];
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunks.push(e.data);
      };
      recordingDone = new Promise<Blob>((resolve) => {
        recorder!.onstop = () => {
          resolve(new Blob(chunks, { type: "video/webm" }));
        };
      });

      recorder.start();
    }

    // Pause the emulator so we can control frame-by-frame via resume ticks
    this.nostalgist.pause();
    await new Promise((r) => setTimeout(r, 50));

    // Now step through the replay: resume for exactly 1 frame at a time,
    // then pause again. The key difference from before is that resume()
    // lets the emulator's own requestAnimationFrame loop run, which
    // actually flushes the WebGL canvas.
    let eventIdx = 0;
    let actionIdx = 0;
    let framesInAction = 0;

    for (let frame = 0; frame <= totalReplayFrames; frame++) {
      // Process all events at this frame
      while (eventIdx < events.length && events[eventIdx]!.frame === frame) {
        const ev = events[eventIdx]!;
        for (const btn of ev.release) {
          this.nostalgist.pressUp(btn);
        }
        for (const btn of ev.press) {
          this.nostalgist.pressDown(btn);
        }
        eventIdx++;
      }

      if (frame >= totalReplayFrames) break;

      // Resume, let one frame render, then pause
      this.nostalgist.resume();
      // Wait for one animation frame — the emulator renders during this
      await new Promise((r) => requestAnimationFrame(r));
      // Small extra yield to ensure the GL draw call completes
      await new Promise((r) => setTimeout(r, 0));
      this.nostalgist.pause();

      // Copy the now-updated WebGL canvas to the 2D recording canvas
      ctx?.drawImage(this.canvas, 0, 0);

      // Track progress
      framesInAction++;
      if (actionIdx < actions.length && framesInAction >= actions[actionIdx]!.frames) {
        framesInAction = 0;
        actionIdx++;
        onProgress?.(actionIdx, actions.length);
      }
    }

    if (!record) return null;

    // Hold the final frame for ~0.5s so the video doesn't cut abruptly
    for (let i = 0; i < 30; i++) {
      ctx?.drawImage(this.canvas, 0, 0);
      await new Promise((r) => requestAnimationFrame(r));
    }

    recorder?.stop();
    const blob = recordingDone ? await recordingDone : null;

    // Clean up
    recordCanvas?.remove();

    return blob;
  }

  async destroy(): Promise<void> {
    if (this.nostalgist) {
      await this.nostalgist.exit();
      this.nostalgist = null;
    }
    this.saveStates.clear();
    this.totalFrames = 0;
  }
}
